import React, { useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '../../components/modal';
import PlatformSiteMarkersForm, { PlatformSiteMarkersFormValues } from './PlatformSiteMarkersForm';
import { PLATFORM_SITES_MODAL_TITLE } from './platformSiteMarkersCopy';

const SheetBackdrop = styled.div`
  ${tw`fixed inset-0 z-50 bg-black/50`}
`;

const SheetPanel = styled.div`
  ${tw`
    fixed inset-x-0 bottom-0 z-[60]
    max-h-[90vh] overflow-y-auto
    rounded-t-2xl bg-white px-5 pb-8 pt-4 shadow-xl
  `}
`;

const SheetHandle = styled.div`
  ${tw`mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300`}
`;

const SheetHeader = styled.div`
  ${tw`mb-4 flex items-center justify-between`}
`;

const SheetTitle = styled.h2`
  ${tw`text-lg font-bold text-gray-900`}
`;

const CloseButton = styled.button`
  ${tw`rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600`}
`;

interface PlatformSiteMarkersEditOverlayProps {
  isOpen: boolean;
  isMobile: boolean;
  saving?: boolean;
  initialValues: PlatformSiteMarkersFormValues;
  onClose: () => void;
  onSubmit: (values: PlatformSiteMarkersFormValues) => void | Promise<void>;
}

export default function PlatformSiteMarkersEditOverlay({
  isOpen,
  isMobile,
  saving = false,
  initialValues,
  onClose,
  onSubmit,
}: PlatformSiteMarkersEditOverlayProps) {
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const form = (
    <PlatformSiteMarkersForm
      initialValues={initialValues}
      saving={saving}
      onCancel={onClose}
      onSubmit={onSubmit}
    />
  );

  if (isMobile) {
    return (
      <>
        <SheetBackdrop onClick={onClose} aria-hidden />
        <SheetPanel
          role="dialog"
          aria-modal="true"
          aria-labelledby="platform-site-markers-sheet-title"
        >
          <SheetHandle />
          <SheetHeader>
            <SheetTitle id="platform-site-markers-sheet-title">
              {PLATFORM_SITES_MODAL_TITLE}
            </SheetTitle>
            <CloseButton type="button" onClick={onClose} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </CloseButton>
          </SheetHeader>
          {form}
        </SheetPanel>
      </>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalWidth="min(560px, calc(100vw - 2rem))">
      <h2 className="mb-4 text-xl font-bold text-gray-900">{PLATFORM_SITES_MODAL_TITLE}</h2>
      {form}
    </Modal>
  );
}
